import { ZCatalogRecordToSave } from "src/business/records/ZCatalogRecord";
import { makeCatalogRecordSafe } from "src/business/records/ZCatalogRecordSafe";
import {
  ZSpecCommon,
  zSpecCommon,
  ZSpecification,
  ZSpecificationTyped,
  zSpecificationTyped,
} from "src/business/specification";
import { getApiUrl } from "src/common/api/getUrl";
import { dataParser } from "src/common/dataParser";
import { mdmLoader } from "src/common/mdmLoader";
import { rest } from "src/common/rest";
import { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import z from "zod";

import { workflowToWrite } from "./workflowToWrite";
import {
  WorkflowsFilter,
  ZWorkflow,
  zWorkflow,
  ZWorkflowCommon,
} from "./ZWorkflow";
import {
  WorkflowCardsFilter,
  ZWorkflowCard,
  zWorkflowCard,
  ZWorkflowCardSafe,
} from "./ZWorkflowCard";
import {
  WorkflowStreamsFilter,
  ZWorkflowStream,
  zWorkflowStream,
} from "./ZWorkflowStream";

const urlWorkflow = (path = "") => getApiUrl(`/core/workflows${path}`);

// Workflows (management)

export const workflowsLoader = (): RtLoaderFn<ZWorkflow, WorkflowsFilter> =>
  mdmLoader(zWorkflow, urlWorkflow());

export const loadWorkflow = async (
  id: string,
  workflowCardId?: string,
): Promise<ZWorkflow> => {
  const resp = await rest.get(urlWorkflow(`/${id}`), {
    params: { workflowCardId },
  });
  return dataParser(() => zWorkflow.parse(resp.data), "loadWorkflow");
};

export const createWorkflow = async (
  data: ZWorkflowCommon,
): Promise<ZWorkflow> => {
  const resp = await rest.post(urlWorkflow(), workflowToWrite(data));
  return dataParser(() => zWorkflow.parse(resp.data), "createWorkflow");
};

export const saveWorkflow = async (
  id: string,
  data: ZWorkflowCommon,
): Promise<ZWorkflow> => {
  const resp = await rest.put(urlWorkflow(`/${id}`), {
    ...workflowToWrite(data),
    id,
  });
  return dataParser(() => zWorkflow.parse(resp.data), "saveWorkflow");
};

export const deleteWorkflow = async (id: string): Promise<void> => {
  await rest.delete(urlWorkflow(`/${id}`));
};

// Workflow streams (user-facing)

export const workflowStreamsLoader = (): RtLoaderFn<
  ZWorkflowStream,
  WorkflowStreamsFilter
> => mdmLoader(zWorkflowStream, urlWorkflow("/streams/list"));

export type WorkflowByCatalogFilter = {};

export const workflowsByCatalogLoader = (
  catalogId: string,
): RtLoaderFn<ZWorkflow, WorkflowByCatalogFilter> =>
  mdmLoader(zWorkflow, urlWorkflow(`/catalog/${catalogId}`));

// Workflow cards

const zWorkflowCardsResponse = z.object({
  content: zWorkflowCard.array(),
  pagination: z.object({ totalElements: z.number() }),
});

const zNextWorkflowStepResponse = z.object({
  catalogCardId: z.string().nullish(),
});

export type ZNextWorkflowStepResponse = z.infer<
  typeof zNextWorkflowStepResponse
>;

export const workflowCardsLoader =
  (
    workflowId: string,
    schemes: ZSpecification[],
  ): RtLoaderFn<ZWorkflowCardSafe, WorkflowCardsFilter> =>
  async (request) => {
    const params = {
      ...request.filters,
      page: request.page,
      size: request.pageSize,
    };
    const resp = await rest.get(urlWorkflow(`/cards/${workflowId}/list`), {
      params,
    });
    const { content, pagination } = dataParser(
      () => zWorkflowCardsResponse.parse(resp.data),
      "workflowCardsLoader",
    );
    const rows: ZWorkflowCardSafe[] = content.map((card) => ({
      ...makeCatalogRecordSafe(card, schemes),
      workflowStep: card.workflowStep,
      workflowId: card.workflowId,
    }));
    return { rows, totalRows: pagination.totalElements };
  };

/** Загрузить карточку рабочего потока */
export const loadWorkflowCard = async (
  cardId: string,
): Promise<ZWorkflowCard> => {
  const resp = await rest.get(urlWorkflow(`/cards/${cardId}`));
  return dataParser(() => zWorkflowCard.parse(resp.data), "loadWorkflowCard");
};

/** Создать карточку рабочего потока */
export const createWorkflowCard = async (
  catalogId: string,
  workflowId: string,
  data: ZCatalogRecordToSave,
): Promise<ZWorkflowCard> => {
  const resp = await rest.post(
    urlWorkflow(`/cards/${catalogId}/${workflowId}`),
    data,
  );
  return dataParser(() => zWorkflowCard.parse(resp.data), "createWorkflowCard");
};

export const createWorkflowCardFromCatalog = async (
  catalogCardId: string,
  workflowId: string,
): Promise<ZWorkflowCard> => {
  const resp = await rest.post(
    urlWorkflow(`/cards/from-catalog/${catalogCardId}/${workflowId}`),
  );
  return dataParser(
    () => zWorkflowCard.parse(resp.data),
    "createWorkflowCardFromCatalog",
  );
};

/** Обновить карточку рабочего потока */
export const updateWorkflowCard = async (
  cardId: string,
  data: ZCatalogRecordToSave,
): Promise<ZWorkflowCard> => {
  const resp = await rest.put(urlWorkflow(`/cards/${cardId}`), data);
  return dataParser(() => zWorkflowCard.parse(resp.data), "updateWorkflowCard");
};

/** Перейти к следующему шагу рабочего потока */
export const nextWorkflowStep = async (
  workflowCardId: string,
  stepId: string,
): Promise<ZNextWorkflowStepResponse> => {
  const resp = await rest.put(
    urlWorkflow(`/cards/next-step/${workflowCardId}`),
    undefined,
    { params: { stepId } },
  );
  return dataParser(
    () => zNextWorkflowStepResponse.parse(resp.data),
    "nextWorkflowStep",
  );
};

/** Загрузить схемы, связанные с шагом РП */
export const loadStepSchemes = async (
  stepId: string,
  classIds: string[],
): Promise<ZSpecificationTyped[]> => {
  const resp = await rest.post(urlWorkflow(`/steps/${stepId}/schemas`), {
    classIds,
  });
  return dataParser(
    () => zSpecificationTyped.array().parse(resp.data),
    "loadStepSchemes",
  );
};

/**
 * Получить все спецификации (первичные/вторичные) для каталога
 * @see https://app.mdm2dev.omndt.ru/api/swagger-ui/index.html#/Primary%20Workflow%20API/getAllSchemas
 */
export const loadAllWorkflowSpecs = async (
  catalogId: string,
): Promise<ZSpecCommon[]> => {
  const resp = await rest.get(urlWorkflow(`/${catalogId}/all`));
  return dataParser(
    () => zSpecCommon.array().parse(resp.data),
    "loadAllWorkflowSpecs",
  );
};
